import { useState } from "react";
import { MsalProvider, MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType, PublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "./auth/msalConfig";
import { useAppRole } from "./auth/useAppRole";
import { Dashboard } from "./pos/Dashboard";
import { POForm } from "./pos/POForm";
import { PODetail } from "./pos/PODetail";
import { usePO } from "./pos/hooks/usePO";
import { AdminPanel } from "./admin/AdminPanel";

type View =
  | { page: "dashboard" }
  | { page: "new-po" }
  | { page: "edit-po"; id: string }
  | { page: "view-po"; id: string }
  | { page: "admin" };

function AccessDenied(): JSX.Element {
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h2>Access Denied</h2>
      <p>Your account has no role assigned in this application.</p>
      <p>Contact your Super Administrator to get access.</p>
    </div>
  );
}

function AuthLoading(): JSX.Element {
  return <div style={{ padding: "2rem" }}>Loading...</div>;
}

function AuthError({ error }: { error: Error | null }): JSX.Element {
  return (
    <div style={{ padding: "2rem" }}>
      <h2>Authentication Error</h2>
      <p>{error?.message ?? "An unknown authentication error occurred."}</p>
    </div>
  );
}

function PODetailLoader({ id, onDone }: { id: string; onDone: () => void }): JSX.Element {
  const { po, loading } = usePO(id);
  if (loading || !po) return <div style={{ padding: "2rem" }}>Loading...</div>;
  return <PODetail po={po} onDone={onDone} />;
}

function MainContent(): JSX.Element {
  const { hasAnyRole, isSuperAdmin, isLoading } = useAppRole();
  const [view, setView] = useState<View>({ page: "dashboard" });
  const goHome = (): void => setView({ page: "dashboard" });

  if (isLoading) return <div style={{ padding: "2rem" }}>Loading...</div>;
  if (!hasAnyRole) return <AccessDenied />;

  if (view.page === "admin") return <AdminPanel onDone={goHome} onViewPO={(id) => setView({ page: "view-po", id })} />;
  if (view.page === "new-po") return <POForm onDone={goHome} />;
  if (view.page === "edit-po") return <POForm id={view.id} onDone={goHome} />;
  if (view.page === "view-po") return <PODetailLoader id={view.id} onDone={goHome} />;
  return (
    <>
      {isSuperAdmin && (
        <div style={{ background: "#1B3A6B", padding: "6px 2rem", textAlign: "right" }}>
          <button
            onClick={() => setView({ page: "admin" })}
            style={{ color: "#fff", background: "none", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 4, cursor: "pointer", fontSize: 13, padding: "4px 14px" }}
          >
            ⚙ Admin Panel
          </button>
        </div>
      )}
      <Dashboard
        onNewPO={() => setView({ page: "new-po" })}
        onEditPO={(id) => setView({ page: "edit-po", id })}
        onViewPO={(id) => setView({ page: "view-po", id })}
      />
    </>
  );
}

interface AppProps {
  msalInstance: PublicClientApplication;
}

function App({ msalInstance }: AppProps): JSX.Element {
  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
        loadingComponent={AuthLoading}
        errorComponent={AuthError}
      >
        <MainContent />
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

export default App;
